/**
 * The Command Centre's menu (Design's sketch "page standard, Today and
 * menu", Nino approved 12 Oct): Today on top, then Build · Content ·
 * People & sessions · Progress · Health, and the business dashboard
 * elsewhere. One place for the menu, the ⌘K palette, the phone top bar's
 * title and each page's title icon.
 */
import {
  CalendarCheck,
  BookOpen,
  Presentation,
  Layers,
  Image as ImageIcon,
  Library,
  Bell,
  Lightbulb,
  Palette,
  Briefcase,
  MapPin,
  GraduationCap,
  UserCog,
  Inbox,
  CalendarDays,
  Radio,
  LineChart,
  ZoomIn,
  ClipboardCheck,
  Workflow,
  AlertOctagon,
  Ruler,
  Activity,
  ListTodo,
  type LucideIcon,
} from 'lucide-react';
import { TOOL, TOOL_NAME, type Tool } from '@/lib/tool';

export type CountKey = 'requests' | 'facilitators' | 'live_drills' | 'review' | 'classes' | 'skill_drills' | 'incidents' | 'today' | 'todo';

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  /** Paths that also light this entry (e.g. its builder under "+ New"). */
  alsoActive?: string[];
  /** A count someone must act on (amber). */
  count?: CountKey;
}

export interface NavGroup {
  name: string;
  items: NavItem[];
}

/**
 * The Command Centre runs drill days (Design's sketch "the split", Nino's go 27 Oct). Classes,
 * drills and programmes moved to the Class Creator; their pages are still reachable here by link.
 */
export const COMMAND_CENTRE_GROUPS: NavGroup[] = [
  {
    name: '',
    items: [
      { name: 'Today', href: '/command-centre', icon: CalendarCheck, count: 'today' },
      // The control tower for a drill day (Nino approved Design's sketch, 18 Oct); replaces Live drills.
      { name: 'Operations', href: '/command-centre/operations', icon: Radio, alsoActive: ['/command-centre/live-drills'], count: 'live_drills' },
    ],
  },
  {
    name: 'Sessions',
    items: [
      { name: 'Requests', href: '/command-centre/request-queue', icon: Inbox, count: 'requests' },
      { name: 'Calendar', href: '/command-centre/calendar', icon: CalendarDays },
      { name: 'Drill reviews', href: '/command-centre/drill-reviews', icon: ClipboardCheck },
    ],
  },
  {
    name: 'People & places',
    items: [
      { name: 'Learners', href: '/command-centre/learners', icon: GraduationCap },
      { name: 'Facilitators', href: '/command-centre/facilitators', icon: UserCog, count: 'facilitators' },
      { name: 'Venues', href: '/command-centre/venues', icon: MapPin },
    ],
  },
  {
    name: 'Progress',
    items: [
      { name: 'Programme progress', href: '/command-centre/programmes', icon: LineChart },
    ],
  },
  {
    name: 'Health',
    items: [
      { name: 'Incidents', href: '/command-centre/ops', icon: AlertOctagon, count: 'incidents' },
      // Is everything we run on healthy? (Nino approved Design's sketch, 15 Oct.)
      { name: 'Systems', href: '/command-centre/systems', icon: Activity },
    ],
  },
];

/** The Class Creator makes and looks after classes, drills and programmes (Design's sketch "the split"). */
export const CLASS_CREATOR_GROUPS: NavGroup[] = [
  {
    name: '',
    items: [
      // "Classes and drills that need work", moved here from the Command Centre's Today.
      { name: 'To do', href: '/to-do', icon: ListTodo, count: 'todo' },
    ],
  },
  {
    name: 'Build',
    items: [
      { name: 'Classes', href: '/micro-class-builder/library', icon: BookOpen, alsoActive: ['/micro-class-builder'], count: 'classes' },
      { name: 'Skill drills', href: '/workshop-builder/library', icon: Presentation, alsoActive: ['/workshop-builder'], count: 'skill_drills' },
      { name: 'Programmes', href: '/scenario-builder/library', icon: Layers, alsoActive: ['/scenario-builder'] },
    ],
  },
  {
    name: 'Words & pictures',
    items: [
      { name: 'Review & wording', href: '/command-centre/catalogue', icon: Library, count: 'review' },
      { name: 'Images', href: '/command-centre/image-library', icon: ImageIcon },
      { name: 'Themes', href: '/command-centre/themes', icon: Palette },
      { name: 'Nudge cards', href: '/command-centre/nudge-cards', icon: Bell },
      { name: 'Knowledge cards', href: '/command-centre/knowledge', icon: Lightbulb },
    ],
  },
  {
    name: 'Coverage',
    items: [
      { name: 'Career library', href: '/command-centre/career-roles', icon: Briefcase },
      // Rubrics left the menu (Nino, 26 Oct: read-only); it opens from Content gaps, which stays lit there.
      { name: 'Content gaps', href: '/command-centre/content-gaps', icon: ZoomIn, alsoActive: ['/command-centre/rubrics'] },
      { name: 'Content pipeline', href: '/command-centre/pipeline', icon: Workflow },
    ],
  },
];

export function navGroupsFor(tool: Tool): NavGroup[] {
  return tool === 'class-creator' ? CLASS_CREATOR_GROUPS : COMMAND_CENTRE_GROUPS;
}

/** This page's menu. */
export const NAV_GROUPS: NavGroup[] = navGroupsFor(TOOL);

/** Which tool a page belongs to, so an old link opens in the right one. */
export function toolOfPath(pathname: string): Tool {
  const inGroups = (groups: NavGroup[]) => groups.some((g) => g.items.some((it) => [it.href, ...(it.alsoActive ?? [])]
    .some((p) => pathname === p || (p !== '/command-centre' && pathname.startsWith(p + '/')))));
  if (pathname === '/to-do' || inGroups(CLASS_CREATOR_GROUPS)) return 'class-creator';
  return 'command-centre';
}

/** "+ New": the three builders. */
export const NEW_ITEMS: NavItem[] = [
  { name: 'New class', href: '/micro-class-builder', icon: BookOpen },
  { name: 'New skill drill', href: '/workshop-builder', icon: Presentation },
  { name: 'New programme', href: '/scenario-builder', icon: Layers },
];

/**
 * The business dashboard (totals, charts and money). Only the preview one
 * exists for now (12 Oct), so every Command Centre opens it; a local copy
 * may point elsewhere with VITE_BUSINESS_DASHBOARD_URL.
 */
export const BUSINESS_DASHBOARD_URL: string =
  (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_BUSINESS_DASHBOARD_URL || 'https://business-dashboard.preview.playbook.plus';

/** Pages that left the menu but still open from another page; still in the ⌘K palette, under their own name. */
export const OFF_MENU: { name: string; href: string; icon: LucideIcon; group: string }[] = [
  { name: 'Rubrics', href: '/command-centre/rubrics', icon: Ruler, group: 'Coverage' },
];

/** Every destination, for the ⌘K palette (menu entries and "+ New"). */
export function getAllNavItems(): { id: string; label: string; group: string; icon: LucideIcon; href: string }[] {
  const out: ReturnType<typeof getAllNavItems> = [];
  for (const g of NAV_GROUPS) for (const it of g.items) out.push({ id: it.href, label: it.name, group: g.name || g.items[0].name, icon: it.icon, href: it.href });
  if (TOOL === 'class-creator') {
    for (const n of NEW_ITEMS) out.push({ id: n.href, label: n.name, group: 'New', icon: n.icon, href: n.href });
    for (const o of OFF_MENU) out.push({ id: o.href, label: o.name, group: o.group, icon: o.icon, href: o.href });
  }
  return out;
}

/** The menu entry a path belongs to (the longest match), or null. */
export function navItemForPath(pathname: string): { item: NavItem; group: string } | null {
  let best: { len: number; item: NavItem; group: string } | null = null;
  for (const g of NAV_GROUPS) {
    for (const item of g.items) {
      for (const p of [item.href, ...(item.alsoActive ?? [])]) {
        const hit = pathname === p || (p !== '/command-centre' && pathname.startsWith(p + '/'));
        if (hit && (!best || p.length > best.len)) best = { len: p.length, item, group: g.name };
      }
    }
  }
  return best ? { item: best.item, group: best.group } : null;
}

/** The menu name for a path, for the phone top bar. */
export function titleForPath(pathname: string): string {
  // The "+ New" pages are named for what they make.
  const made = NEW_ITEMS.find((n) => pathname === n.href || (n.href === '/workshop-builder' && pathname === '/workshop-builder/plots'));
  if (made) return made.name;
  const off = OFF_MENU.find((o) => pathname === o.href || pathname.startsWith(o.href + '/'));
  if (off) return off.name;
  return navItemForPath(pathname)?.item.name ?? TOOL_NAME[TOOL];
}

export function isActive(item: NavItem, pathname: string): boolean {
  return navItemForPath(pathname)?.item.href === item.href;
}
